import { useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Images, CheckCircle, AlertTriangle, X, Loader2, FolderUp } from "lucide-react";

/**
 * Batch photo upload that matches images to inventory items by SKU in filename.
 * Patterns supported:
 *   • <SKU>.<ext>          — one photo per item (or first of a sequence)
 *   • <SKU>-<n>.<ext>      — multiple photos per item, ordered by n
 */
export default function BatchPhotosImport() {
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("append");
  const [dragOver, setDragOver] = useState(false);

  const onPickFiles = (chosen) => {
    if (!chosen?.length) return;
    const images = Array.from(chosen).filter((f) => /^image\//.test(f.type) || /\.(jpe?g|png|webp|gif)$/i.test(f.name));
    if (!images.length) {
      toast.error("no image files detected.");
      return;
    }
    setFiles(images);
    setPreview(null);
    setResult(null);
  };

  const runPreview = async () => {
    if (!files.length) {
      toast.error("choose images first.");
      return;
    }
    setPreviewing(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      const { data } = await api.post("/inventory/import/photos/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "preview failed.");
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!files.length) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      files.forEach((f) => fd.append("files", f, f.name));
      const { data } = await api.post("/inventory/import/photos/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`uploaded ${data.uploaded} photo${data.uploaded === 1 ? "" : "s"} across ${data.items_touched} pieces.`);
      setFiles([]);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "upload failed.");
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    setFiles([]);
    setPreview(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
    if (folderRef.current) folderRef.current.value = "";
  };

  const dropHandler = (e) => {
    e.preventDefault();
    setDragOver(false);
    onPickFiles(e.dataTransfer.files);
  };

  return (
    <section data-testid="batch-photos-section">
      <div className="bg-linen border border-taupe/50 p-6 sm:p-10">
        <p className="editorial-label mb-3">naming convention</p>
        <p className="text-xs text-espresso leading-relaxed mb-2">
          name each photo with the piece&apos;s <strong>SKU</strong>. both patterns are supported:
        </p>
        <ul className="text-xs text-espresso leading-relaxed list-disc pl-5 mb-6">
          <li><span className="font-mono bg-ivory border border-taupe/40 px-2 py-0.5">0001.jpg</span> — one photo for sku 0001</li>
          <li><span className="font-mono bg-ivory border border-taupe/40 px-2 py-0.5">0001-1.jpg</span>, <span className="font-mono bg-ivory border border-taupe/40 px-2 py-0.5">0001-2.jpg</span> — multiple photos for sku 0001 (ordered by suffix)</li>
        </ul>

        {!files.length ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={dropHandler}
            className={`border-2 border-dashed p-10 text-center transition-colors ${dragOver ? "border-brass bg-ivory" : "border-taupe hover:border-brass"}`}
            data-testid="batch-photos-drop"
          >
            <Images className="w-8 h-8 text-taupe mx-auto mb-3" />
            <p className="font-serif text-xl sm:text-2xl lowercase text-charcoal mb-6">drop photos or a folder here</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => inputRef.current?.click()}
                data-testid="batch-pick-files"
                className="border border-brass text-brass hover:bg-brass hover:text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> choose files
              </button>
              <button
                onClick={() => folderRef.current?.click()}
                data-testid="batch-pick-folder"
                className="border border-taupe text-charcoal hover:border-brass hover:text-brass px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2"
              >
                <FolderUp className="w-4 h-4" /> choose folder
              </button>
            </div>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPickFiles(e.target.files)} />
            <input
              ref={folderRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
              {...{ webkitdirectory: "", directory: "" }}
            />
          </div>
        ) : (
          <div data-testid="batch-photos-staged">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <p className="font-serif text-2xl lowercase text-charcoal">{files.length} photo{files.length === 1 ? "" : "s"} ready</p>
              <div className="flex gap-2">
                <button onClick={reset} data-testid="batch-reset" className="border border-taupe text-charcoal px-4 py-2 text-xs tracking-[0.25em] uppercase">reset</button>
                <button onClick={runPreview} disabled={previewing} data-testid="batch-preview-btn" className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-xs tracking-[0.25em] uppercase disabled:opacity-50 flex items-center gap-2">
                  {previewing && <Loader2 className="w-3 h-3 animate-spin" />} match by sku
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2 max-h-72 overflow-y-auto p-2 bg-ivory border border-taupe/40">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="relative aspect-square bg-linen border border-taupe/30">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-charcoal/80 text-ivory text-[0.55rem] tracking-wider px-1 py-0.5 truncate">{f.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <div className="mt-8" data-testid="batch-preview">
          <div className="bg-linen border border-taupe/50 p-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
            <div>
              <p className="editorial-label">match · {preview.total_files} files</p>
              <p className="font-serif text-2xl lowercase text-charcoal mt-1">
                <CheckCircle className="w-4 h-4 inline text-brass" /> {preview.matched_count} matched
                <AlertTriangle className="w-4 h-4 inline text-red-700 ml-4" /> {preview.unmatched_count} without a home
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <fieldset className="flex items-center gap-3 border border-taupe/50 px-4 py-2" data-testid="batch-mode">
                <legend className="px-2 editorial-label" style={{ fontSize: "0.55rem" }}>on existing photos</legend>
                <label className="text-xs text-charcoal flex items-center gap-1.5"><input type="radio" name="mode" value="append" checked={mode === "append"} onChange={() => setMode("append")} data-testid="mode-append" /> append</label>
                <label className="text-xs text-charcoal flex items-center gap-1.5"><input type="radio" name="mode" value="replace" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="mode-replace" /> replace</label>
              </fieldset>
              <button onClick={commit} disabled={committing || preview.matched_count === 0} data-testid="batch-commit" className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase disabled:opacity-50 flex items-center gap-2">
                {committing && <Loader2 className="w-3 h-3 animate-spin" />} attach {preview.matched_count} photo{preview.matched_count === 1 ? "" : "s"}
              </button>
            </div>
          </div>

          {preview.matched_groups.length > 0 && (
            <div className="border border-taupe/40 mb-4">
              <div className="bg-linen px-4 py-3 editorial-label">matched · by sku</div>
              <table className="w-full text-xs">
                <thead className="bg-ivory">
                  <tr className="text-left text-taupe">
                    <th className="px-3 py-2">sku</th>
                    <th className="px-3 py-2">piece</th>
                    <th className="px-3 py-2">existing</th>
                    <th className="px-3 py-2">incoming</th>
                    <th className="px-3 py-2">files</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.matched_groups.map((g) => (
                    <tr key={g.item_id} className="border-t border-taupe/30" data-testid={`match-${g.sku}`}>
                      <td className="px-3 py-2 font-mono text-charcoal">{g.sku}</td>
                      <td className="px-3 py-2 text-charcoal font-serif lowercase">{g.title || "—"}</td>
                      <td className="px-3 py-2 text-taupe tabular-nums">{g.existing_photos}</td>
                      <td className="px-3 py-2 text-brass tabular-nums">+{g.incoming_count}</td>
                      <td className="px-3 py-2 text-xs text-espresso">{g.files.map((f) => f.filename).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.unmatched_files.length > 0 && (
            <div className="border border-red-200">
              <div className="bg-red-50 px-4 py-3 editorial-label text-red-800">unmatched · {preview.unmatched_files.length}</div>
              <ul className="divide-y divide-red-100 text-xs">
                {preview.unmatched_files.map((u) => (
                  <li key={u.filename} className="px-4 py-2 flex items-center gap-3" data-testid="unmatched-row">
                    <X className="w-3 h-3 text-red-700" />
                    <span className="font-mono">{u.filename}</span>
                    <span className="text-red-700">— {u.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Commit result */}
      {result && (
        <div className="mt-8 bg-linen border border-brass/40 p-6" data-testid="batch-result">
          <p className="editorial-label">import complete</p>
          <p className="font-serif text-2xl lowercase text-charcoal mt-1">
            {result.uploaded} photo{result.uploaded === 1 ? "" : "s"} attached · {result.items_touched} piece{result.items_touched === 1 ? "" : "s"} updated · mode <span className="text-brass">{result.mode}</span>
          </p>
          {result.unmatched_count > 0 && (
            <p className="text-xs text-red-700 mt-2">{result.unmatched_count} file{result.unmatched_count === 1 ? "" : "s"} skipped (no matching sku).</p>
          )}
        </div>
      )}
    </section>
  );
}
